// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  forwardRef,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
} from 'react';
import { AppIcon, useInfoPopup, InfoTooltip } from '@shared/ui-kit/components';
import { Typography } from '@mui/material';
import type { PopupsName } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { isUndefined } from '@shared/utils';

/** Props for the ChartContainer component. */
export interface ChartContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** The header content of the chart container. */
  readonly header?: ReactNode;

  /** Popup name used for displaying alerts. */
  readonly popupName?: PopupsName;

  readonly headerRight?: ReactNode;

  readonly tooltip?: boolean;

  readonly divider?: boolean;

  readonly fullHeight?: boolean;
}

/**
 * A container component for displaying charts.
 *
 * @returns - The rendered ChartContainer component.
 * @component
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(
  (
    {
      children,
      popupName,
      header,
      headerRight,
      className = '',
      tooltip,
      divider = true,
      fullHeight = false,
      ...props
    },
    ref,
  ) => {
    const infoPopups = useInfoPopup();

    /**
     * Event handler for the info icon click.
     *
     * @param {React.MouseEvent<SVGElement>} e - The click event.
     */
    const onInfoClick: MouseEventHandler<SVGElement> = useCallback(
      e => {
        e.stopPropagation();

        // Display an alert if popupName is defined
        if (!isUndefined(popupName)) infoPopups(popupName);
      },
      [infoPopups, popupName],
    );

    return (
      <div
        className={classNames(
          'grid h-full grid-cols-1 gap-3 rounded-lg bg-section px-6 py-4',
          { '!gap-2': !divider },
          { 'grid-rows-[auto_auto_1fr]': divider },
          { 'grid-rows-[auto_1fr]': !divider },
          className,
        )}
        {...props}
        ref={ref}
      >
        <header className="flex justify-between">
          <div className="flex flex-row items-center space-x-4">
            <Typography
              variant="h2"
              className="!font-medium"
              data-testid="chart-container-title"
            >
              {header}
            </Typography>
            {/* Render the info icon if alertOptions is defined */}
            {!isUndefined(popupName) ? (
              tooltip ? (
                <InfoTooltip title={popupName}>
                  <AppIcon
                    icon="Info"
                    size="base"
                    color="disabled"
                    data-testid={`chart-container-icon-${header}`}
                    className="cursor-pointer"
                  />
                </InfoTooltip>
              ) : (
                <AppIcon
                  icon="Info"
                  size="base"
                  color="disabled"
                  data-testid={`chart-container-icon-${header}`}
                  className="cursor-pointer"
                  onClick={onInfoClick}
                />
              )
            ) : null}
          </div>
          {/* Render aditional info at the right*/}
          {!isUndefined(headerRight) ? <div>{headerRight}</div> : null}
        </header>
        {divider && <hr className="h-px border-none bg-border-sep" />}
        <div
          className={classNames(
            'chart-container mt-3 flex flex-col items-stretch justify-stretch',
            { '!mt-0': !divider },
            { 'min-h-0 flex-1': fullHeight },
            { 'min-h-[280px]': !fullHeight },
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);

// Set the display name for the ChartContainer component
ChartContainer.displayName = 'ChartContainer';

export { ChartContainer };
